// src/pages/Marketplace/shared/useSurgeEvents.ts
import { useCallback, useEffect, useState } from 'react';
import type { SurgeEvent } from './types';

export interface SurgeEventBody {
  market_id: string;
  name: string;
  multiplier: number;
  starts_at: string;   // UTC ISO
  ends_at: string;     // UTC ISO
  notes?: string | null;
}

export function useSurgeEvents(
  apiUrl: string,
  accessToken: string | null,
  marketId: string | null,
  includeExpired: boolean,
) {
  const [data, setData] = useState<SurgeEvent[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!apiUrl || !accessToken || !marketId) return;
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams({
        marketId,
        includeExpired: includeExpired ? 'true' : 'false',
      });
      const res = await fetch(`${apiUrl}/admin/surge/events?${qs}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error(`GET surge events ${res.status}`);
      const body = (await res.json()) as { items: SurgeEvent[] };
      setData(body.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally { setLoading(false); }
  }, [apiUrl, accessToken, marketId, includeExpired]);

  useEffect(() => { refetch(); }, [refetch]);

  return { data, loading, error, refetch };
}

export function useSurgeEventMutations(apiUrl: string, accessToken: string | null) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const auth = (): HeadersInit => ({
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  });

  const create = async (body: SurgeEventBody): Promise<SurgeEvent | null> => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${apiUrl}/admin/surge/events`, {
        method: 'POST', headers: auth(), body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.text()) || `POST ${res.status}`);
      return (await res.json()) as SurgeEvent;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error'); return null;
    } finally { setLoading(false); }
  };

  const update = async (id: string, body: Partial<SurgeEventBody>): Promise<SurgeEvent | null> => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${apiUrl}/admin/surge/events/${id}`, {
        method: 'PUT', headers: auth(), body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.text()) || `PUT ${res.status}`);
      return (await res.json()) as SurgeEvent;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error'); return null;
    } finally { setLoading(false); }
  };

  const softDelete = async (id: string): Promise<boolean> => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${apiUrl}/admin/surge/events/${id}`, {
        method: 'DELETE', headers: auth(),
      });
      if (!res.ok) throw new Error((await res.text()) || `DELETE ${res.status}`);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error'); return false;
    } finally { setLoading(false); }
  };

  return { loading, error, create, update, softDelete };
}
